import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import clsx from 'clsx'

import { data, useClock, useCreateMailRule, useMailAudit } from '../lib/data'
import type { MailAuditProposedRule, MailTriageItem } from '../lib/data/types'
import { PILE_WORD, auditWeekStart, ruleSentence, senderShort, typingInto, weekRange } from '../components/day/designed'
import { CheckGlyph, Sparkle } from '../components/day/icons'
import { DayCard, Kc, SecHead, Sk, Tip } from '../components/day/parts'
import { DayBand } from '../components/day/sections'
import { markWeeklyDone, useDayScenario } from './Day'

// ============================================================================
// THE WEEKLY CHECK — docs/design/handoff/day-page/Audit.dc.html (board 5).
//
// Last week's numbers as plain numbers, ten items at random from the folded piles ("did any of
// these matter?" — one tap each), and the rules Claude proposes from the week's moves. Nothing
// changes until the owner answers. Day ↔ Weekly check is an instant cut (14h): a route, no motion.
//
// GRADING IS THE EXISTING VERDICT CALL: "No" confirms the verdict it already has; "Mattered" moves
// it to Important. Accepting a rule is `POST /api/mail/rules`. Rejecting one sends nothing — the
// API has no rejection — and says so by simply setting the row aside.
//
// NOT DRAWN, ON PURPOSE: the board's "3 you replied to from the noise pile". LifeOS does not read
// sent mail, the audit API carries no such number, and a figure with nothing behind it would be
// the app faking knowledge (CLAUDE.md → Design system).
// ============================================================================

type Grade = 'yes' | 'no'

export function DayWeeklyCheck() {
  const scenario = useDayScenario()
  const navigate = useNavigate()
  const { data: clock } = useClock()
  const weekStart = clock ? auditWeekStart(clock.date) : undefined
  const audit = useMailAudit(weekStart, scenario)
  const createRule = useCreateMailRule()
  const back = scenario ? `/day?state=${scenario}` : '/day'

  // The sample is FROZEN on arrival: grading an item re-files it, and a list that re-shuffles under
  // the cursor while it is being answered is a list nobody can finish.
  const [sample, setSample] = useState<MailTriageItem[] | null>(null)
  useEffect(() => {
    if (audit.data && sample === null) setSample(audit.data.sample)
  }, [audit.data, sample])

  const [grades, setGrades] = useState<Record<string, Grade>>({})
  const [rules, setRules] = useState<Record<string, 'accepted' | 'rejected'>>({})
  const [error, setError] = useState<string | null>(null)
  const [focus, setFocus] = useState(0)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])

  const grade = (item: MailTriageItem, g: Grade) => {
    setGrades((cur) => ({ ...cur, [item.id]: g }))
    setError(null)
    const verdict = g === 'yes' ? 'important' : item.verdict
    if (!verdict) return
    data.setMailVerdict(item.id, verdict).catch((e: unknown) => {
      setGrades((cur) => {
        const { [item.id]: _undone, ...rest } = cur
        return rest
      })
      setError(e instanceof Error ? e.message : 'That answer was not saved. Try again.')
    })
  }

  const accept = (rule: MailAuditProposedRule) => {
    const k = `${rule.matchType}:${rule.matchValue}`
    setRules((cur) => ({ ...cur, [k]: 'accepted' }))
    createRule.mutate(
      { matchType: rule.matchType, matchValue: rule.matchValue, verdict: rule.verdict },
      {
        onError: (e) => {
          setRules(({ [k]: _undone, ...rest }) => rest)
          setError(e instanceof Error ? e.message : 'The rule was not saved. Try again.')
        },
      },
    )
  }

  const finish = () => {
    if (weekStart) markWeeklyDone(weekStart)
    navigate(back)
  }

  // J/K between rows, Y/N to answer the focused one.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || typingInto(e.target) || !sample) return
      const k = e.key.toLowerCase()
      if (k === 'j' || k === 'k') {
        e.preventDefault()
        const next = Math.max(0, Math.min(sample.length - 1, focus + (k === 'j' ? 1 : -1)))
        setFocus(next)
        rowRefs.current[next]?.focus()
      } else if ((k === 'y' || k === 'n') && sample[focus]) {
        e.preventDefault()
        grade(sample[focus]!, k === 'y' ? 'yes' : 'no')
        const next = Math.min(sample.length - 1, focus + 1)
        setFocus(next)
        rowRefs.current[next]?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sample, focus])

  const a = audit.data
  const graded = sample ? sample.filter((s) => grades[s.id]).length : 0
  const total = sample?.length ?? 0

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-page">
      <DayBand
        title={
          <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 text-[15px] font-semibold tracking-[-0.01em]">
            <Link to={back} className="text-muted no-underline hover:text-ink">
              Day
            </Link>
            <span className="text-faint">/</span>
            <h1 className="m-0 text-[15px] font-semibold leading-none text-ink">Weekly check</h1>
          </nav>
        }
        summary={a ? <span className="text-[11.5px] text-muted">{weekRange(a.weekStart, a.weekEnd)} · about 2 minutes</span> : <Sk w={140} h={10} />}
        right={
          <button type="button" onClick={finish} className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] text-muted underline hover:text-ink">
            Skip this week
          </button>
        }
      />

      <main className="box-border flex w-full max-w-[820px] flex-col gap-[18px] px-[30px] pb-[60px] pt-[22px]">
        {audit.error ? (
          <p className="m-0 text-[12px] text-danger">{audit.error instanceof Error ? audit.error.message : 'The weekly check could not be read.'}</p>
        ) : !a || !sample ? (
          <DayCard aria-label="Weekly check, loading" className="px-4 py-4">
            <div className="flex flex-col gap-3">
              <Sk w="40%" h={11} />
              <Sk w="70%" h={10} second />
              <Sk w="55%" h={10} second />
            </div>
          </DayCard>
        ) : (
          <>
            <section aria-label="This week in numbers" className="flex flex-wrap gap-x-10 gap-y-[10px] px-[2px] pb-1 pt-[6px]">
              <Figure n={a.counts.sorted} label="sorted" />
              <Figure n={a.counts.moved} label="you moved" />
            </section>

            <DayCard aria-labelledby="a-h">
              <SecHead id="a-h" title="Did any of these matter?" count={`${graded} of ${total}`} note="ten at random from low priority and noise" />
              <div className="h-[2px] bg-line-3">
                <div className="h-[2px] bg-accent transition-[width] duration-[var(--dur-hover)] ease-[var(--ease-ink)]" style={{ width: total ? `${(graded / total) * 100}%` : '0%' }} />
              </div>
              {sample.length === 0 && <p className="m-0 border-t border-line-3 px-4 py-3 text-[12px] text-ink-3">Nothing was folded last week, so there is nothing to check.</p>}
              {sample.map((item, i) => {
                const g = grades[item.id]
                return (
                  <div
                    key={item.id}
                    ref={(el) => {
                      rowRefs.current[i] = el
                    }}
                    tabIndex={0}
                    onFocus={() => setFocus(i)}
                    className="group flex flex-wrap items-center gap-x-3 gap-y-[6px] border-t border-line-3 px-4 py-[10px] focus-visible:bg-surface-2"
                  >
                    <span className="w-[120px] flex-none truncate text-[11.5px] text-ink-3">{senderShort(item)}</span>
                    <span className="min-w-0 flex-[1_1_220px] truncate text-[12px] text-ink-2">{item.subject}</span>
                    {item.verdict && (
                      <span className="whitespace-nowrap rounded-pill border border-line px-2 py-px text-[10.5px] font-semibold text-muted">{PILE_WORD[item.verdict]}</span>
                    )}
                    <div className="flex gap-[6px]">
                      <button
                        type="button"
                        aria-pressed={g === 'yes'}
                        onClick={() => grade(item, 'yes')}
                        className={clsx('day-btn day-tipw px-[11px]', g === 'yes' && 'border-success-line bg-success-fill font-semibold text-success hover:bg-success-fill')}
                      >
                        <Tip name="Mattered" k="Y" align="right">
                          Moves it to Important. Corrects Claude's sorting.
                        </Tip>
                        {g === 'yes' && <CheckGlyph size={14} />}
                        Mattered
                        {!g && i === focus && (
                          <span className="hidden group-focus-visible:inline-flex">
                            <Kc>Y</Kc>
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        aria-pressed={g === 'no'}
                        onClick={() => grade(item, 'no')}
                        className={clsx('day-btn day-tipw px-[11px]', g === 'no' && 'border-line-2 bg-fill font-semibold text-ink hover:bg-fill')}
                      >
                        <Tip name="No" k="N" align="right">
                          Keeps it where Claude sorted it, and says the sort was right.
                        </Tip>
                        No
                        {!g && i === focus && (
                          <span className="hidden group-focus-visible:inline-flex">
                            <Kc>N</Kc>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                )
              })}
            </DayCard>

            {a.proposedRules.length > 0 && (
              <DayCard aria-labelledby="r-h">
                <SecHead
                  id="r-h"
                  icon={<Sparkle className="text-chat" />}
                  title="Proposed rules"
                  count={a.proposedRules.length}
                  note="proposed by Claude from this week's moves · nothing changes until you accept"
                />
                {a.proposedRules.map((r) => {
                  const k = `${r.matchType}:${r.matchValue}`
                  const state = rules[k]
                  return (
                    <div key={k} className="flex flex-wrap items-start gap-x-3 gap-y-[6px] border-t border-line-3 px-4 py-[13px]">
                      <div className="flex min-w-0 flex-[1_1_360px] flex-col gap-1">
                        <span className="text-[12.5px] font-semibold">{ruleSentence(r)}</span>
                        <span className="text-[11.5px] leading-[1.45] text-ink-3">{r.evidence}</span>
                      </div>
                      {state ? (
                        <span className={clsx('text-[11.5px] font-semibold', state === 'accepted' ? 'text-success' : 'text-muted')}>
                          {state === 'accepted' ? 'Accepted' : 'Rejected'}
                        </span>
                      ) : (
                        <div className="flex gap-[6px]">
                          <button type="button" className="day-btn day-btn-solid day-tipw px-[11px]" onClick={() => accept(r)}>
                            Accept
                            <Tip name="Accept" align="right">
                              Files this sender's mail this way from now on. Nothing changes in Gmail.
                            </Tip>
                          </button>
                          <button type="button" className="day-btn day-tipw px-[11px]" onClick={() => setRules((cur) => ({ ...cur, [k]: 'rejected' }))}>
                            Reject
                            <Tip name="Reject" align="right">
                              Sets it aside. Nothing is saved and nothing changes.
                            </Tip>
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </DayCard>
            )}

            {error && <p className="m-0 text-[12px] text-danger">{error}</p>}

            <div className="flex justify-end">
              <button type="button" onClick={finish} className="day-btn day-btn-solid h-[34px] px-4">
                Done
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  )
}

function Figure({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex flex-col gap-[6px]">
      <span className="text-[30px] font-light leading-[1.2] tracking-[-0.02em] text-ink">{n}</span>
      <span className="text-[11.5px] text-muted">{label}</span>
    </div>
  )
}
